import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dialog } from '@nexply/ui';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { cropToDataUrl, fileToBitmap } from '../lib/image.js';

interface Props {
  file: File | null;
  onCancel: () => void;
  onConfirm: (dataUrl: string) => void;
}

/** Kantenlänge der quadratischen Vorschau in CSS-Pixeln. */
const VIEW = 288;
const OUT_SIZE = 512;
const QUALITY = 0.85;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

export function ImageCropDialog({ file, onCancel, onConfirm }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bitmapRef = useRef<ImageBitmap | null>(null);
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  const [ready, setReady] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  // Position der linken oberen Bildecke im Vorschau-Koordinatensystem.
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Deckt das Bild bei zoom=1 exakt das Vorschau-Quadrat ab (cover).
  const scaleFor = useCallback((z: number) => {
    const bmp = bitmapRef.current;
    if (!bmp) return 1;
    return (VIEW / Math.min(bmp.width, bmp.height)) * z;
  }, []);

  const clamp = useCallback((x: number, y: number, z: number) => {
    const bmp = bitmapRef.current;
    if (!bmp) return { x, y };
    const s = scaleFor(z);
    const dw = bmp.width * s;
    const dh = bmp.height * s;
    return {
      x: Math.min(0, Math.max(VIEW - dw, x)),
      y: Math.min(0, Math.max(VIEW - dh, y)),
    };
  }, [scaleFor]);

  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    setReady(false);
    setErr(null);
    void (async () => {
      try {
        const bmp = await fileToBitmap(file);
        if (cancelled) {
          bmp.close?.();
          return;
        }
        bitmapRef.current = bmp;
        const s = (VIEW / Math.min(bmp.width, bmp.height)) * 1;
        setZoom(1);
        setOffset({
          x: (VIEW - bmp.width * s) / 2,
          y: (VIEW - bmp.height * s) / 2,
        });
        setReady(true);
      } catch (x) {
        if (!cancelled) setErr((x as Error).message || 'Bild konnte nicht geladen werden.');
      }
    })();
    return () => {
      cancelled = true;
      bitmapRef.current?.close?.();
      bitmapRef.current = null;
    };
  }, [file]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const bmp = bitmapRef.current;
    if (!ready || !canvas || !bmp) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    canvas.width = VIEW * dpr;
    canvas.height = VIEW * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, VIEW, VIEW);

    const s = scaleFor(zoom);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bmp, offset.x, offset.y, bmp.width * s, bmp.height * s);

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, VIEW, VIEW);
    ctx.arc(VIEW / 2, VIEW / 2, VIEW / 2, 0, Math.PI * 2, true);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fill();
    ctx.restore();

    ctx.beginPath();
    ctx.arc(VIEW / 2, VIEW / 2, VIEW / 2 - 1, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [ready, zoom, offset, scaleFor]);

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!ready) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    const d = dragRef.current;
    if (!d) return;
    setOffset(clamp(d.ox + (e.clientX - d.x), d.oy + (e.clientY - d.y), zoom));
  }

  function onPointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
  }

  function changeZoom(next: number) {
    const z = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
    const ratio = scaleFor(z) / scaleFor(zoom);
    // Der Punkt in der Mitte der Vorschau bleibt beim Zoomen stehen.
    const x = VIEW / 2 - (VIEW / 2 - offset.x) * ratio;
    const y = VIEW / 2 - (VIEW / 2 - offset.y) * ratio;
    setZoom(z);
    setOffset(clamp(x, y, z));
  }

  function confirm() {
    const bmp = bitmapRef.current;
    if (!bmp) return;
    const s = scaleFor(zoom);
    const dataUrl = cropToDataUrl(
      bmp,
      { sx: -offset.x / s, sy: -offset.y / s, size: VIEW / s },
      OUT_SIZE,
      QUALITY,
    );
    onConfirm(dataUrl);
  }

  return (
    <Dialog
      open={!!file}
      onClose={onCancel}
      size="sm"
      title="Bild zuschneiden"
      description="Ziehen zum Verschieben, Regler zum Zoomen."
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Abbrechen
          </Button>
          <Button type="button" disabled={!ready} onClick={confirm}>
            Übernehmen
          </Button>
        </div>
      }
    >
      <div className="flex flex-col items-center gap-5">
        <div
          className="relative overflow-hidden rounded-2xl border border-line bg-surface-3"
          style={{ width: VIEW, height: VIEW }}
        >
          <canvas
            ref={canvasRef}
            style={{ width: VIEW, height: VIEW, touchAction: 'none' }}
            className="cursor-grab active:cursor-grabbing"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
          {!ready && !err && (
            <div className="absolute inset-0 grid place-items-center text-sm text-text-dim">
              Lade Bild…
            </div>
          )}
        </div>

        <div className="flex w-full items-center gap-3" style={{ maxWidth: VIEW }}>
          <ZoomOut className="h-4 w-4 shrink-0 text-text-dim" />
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            disabled={!ready}
            onChange={(e) => changeZoom(Number(e.target.value))}
            className="w-full accent-gold"
            aria-label="Zoom"
          />
          <ZoomIn className="h-4 w-4 shrink-0 text-text-dim" />
        </div>

        {err && <div className="text-xs text-danger">{err}</div>}
      </div>
    </Dialog>
  );
}
